/**
 * Recall Check — dealer dashboard page.
 *
 * Three things a dealer needs: their install snippet + hosted page link,
 * the batch VIN upload tool with job status, and the leads the tool has
 * generated. Everything here is entitlement-gated server-side too.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE } from "../api/config";
import {
  Loader2, Upload, Copy, ExternalLink, RefreshCw, Download, ShieldOff,
  FileSpreadsheet, AlertTriangle,
} from "lucide-react";
import { STATE_COLORS } from "./theme";

const fmt = (dt) => {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return dt; }
};

export default function RecallDealerPage() {
  const { apiFetch } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const s = await apiFetch("/dealer/recall/settings", { method: "GET" });
      setSettings(s?.data || null);
      if (s?.data?.enabled) {
        const [j, l] = await Promise.all([
          apiFetch("/dealer/recall/batch", { method: "GET" }),
          apiFetch("/dealer/recall/leads?limit=200", { method: "GET" }),
        ]);
        setJobs(j?.data?.rows || []);
        setLeads(l?.data?.rows || []);
      }
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  // Poll while any job is still running.
  useEffect(() => {
    const active = jobs.some((j) => j.status === "queued" || j.status === "running");
    if (!active) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [jobs, load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadErr("");
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await fetch(`${API_BASE}/dealer/recall/batch`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof json.detail === "string" ? json.detail : "Upload failed");
      await load();
    } catch (e) {
      setUploadErr(e.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadReport = async (jobId) => {
    const res = await fetch(`${API_BASE}/dealer/recall/batch/${jobId}/report.csv`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `recall-report-${jobId.slice(0, 8)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-[13px] p-8">
        <Loader2 className="animate-spin" size={16} /> Loading Recall Check…
      </div>
    );
  }

  if (!settings?.enabled) {
    return (
      <div className="max-w-xl bg-white rounded-lg border border-gray-200 p-8 text-center" data-testid="recall-dealer-disabled">
        <ShieldOff className="mx-auto text-gray-300" size={30} />
        <h2 className="text-[17px] font-semibold text-gray-900 mt-3">Recall Check isn&apos;t active on your account</h2>
        <p className="text-[13px] text-gray-500 mt-2">
          Recall Check is an add-on product. Contact your account manager to switch it on.
        </p>
      </div>
    );
  }

  const cfg = settings.config || {};
  const ref = settings.dealer_ref;
  const snippet = `<script src="${window.location.origin}/api/recall-widget.js" data-dealer="${ref}" data-mode="popup"></script>`;
  const pageUrl = cfg.slug ? `${window.location.origin}/recall/${cfg.slug}` : null;

  return (
    <div className="space-y-5" data-testid="recall-dealer-page">
      <div>
        <h1 className="text-[22px] font-bold text-gray-900 tracking-tight">Recall Check</h1>
        <p className="text-[13px] text-gray-500 mt-1 max-w-2xl">
          A free VIN recall lookup for your customers — and a warm service lead every time an open
          recall is found. Works on your website, on a hosted page, and against your own VIN export.
        </p>
      </div>

      {/* Install */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="text-[14px] font-semibold text-gray-900">On your website</h3>
          <p className="text-[12px] text-gray-500 mt-1 mb-3">
            Paste this once, anywhere in your site template. It adds a floating &quot;Free recall check&quot;
            button.
          </p>
          <code className="block text-[11px] bg-gray-900 text-gray-100 rounded-md px-3 py-2.5 overflow-x-auto whitespace-nowrap" data-testid="recall-dealer-snippet">
            {snippet}
          </code>
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(snippet)}
            className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] rounded-md border border-gray-200 px-3 py-1.5 hover:bg-gray-50"
            data-testid="recall-dealer-copy-snippet"
          >
            <Copy size={13} /> Copy snippet
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="text-[14px] font-semibold text-gray-900">Hosted page</h3>
          <p className="text-[12px] text-gray-500 mt-1 mb-3">
            No website changes needed — share this link in email, SMS or social.
          </p>
          {pageUrl ? (
            <a href={pageUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[13px] text-blue-700 hover:underline" data-testid="recall-dealer-page-link">
              {pageUrl} <ExternalLink size={13} />
            </a>
          ) : (
            <p className="text-[12.5px] text-amber-700 flex items-center gap-1.5">
              <AlertTriangle size={14} /> Your hosted page hasn&apos;t been named yet — ask your account manager.
            </p>
          )}
        </div>
      </div>

      {/* Batch */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-[14px] font-semibold text-gray-900 flex items-center gap-2">
              <FileSpreadsheet size={15} className="text-gray-400" /> Batch VIN check
            </h3>
            <p className="text-[12px] text-gray-500 mt-1 max-w-xl">
              Upload your own VIN export (CSV or Excel). We detect the VIN column automatically —
              no template needed. Include name / phone / email columns and any open-recall vehicle
              becomes a follow-up lead.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
              data-testid="recall-batch-file-input"
            />
            <button
              type="button" disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-md bg-gray-900 text-white text-[13px] font-semibold px-4 py-2 hover:bg-gray-800 disabled:opacity-60"
              data-testid="recall-batch-upload-btn"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? "Uploading…" : "Upload VIN file"}
            </button>
            <button
              type="button" onClick={load}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-2 text-[12.5px] hover:bg-gray-50"
              data-testid="recall-refresh-btn"
            >
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
        </div>

        {uploadErr && (
          <div className="mt-3 text-[12.5px] text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="recall-batch-error">
            {uploadErr}
          </div>
        )}

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="recall-jobs-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
                <th className="py-2 pr-3">File</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Progress</th>
                <th className="py-2 pr-3">Open recalls</th>
                <th className="py-2 pr-3">Clear</th>
                <th className="py-2 pr-3">Not available</th>
                <th className="py-2 pr-3">Uploaded</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-gray-400">No batch checks yet.</td></tr>
              )}
              {jobs.map((j) => (
                <tr key={j.id} className="border-b border-gray-50" data-testid={`recall-job-row-${j.id}`}>
                  <td className="py-2.5 pr-3 font-medium text-gray-900">{j.filename}</td>
                  <td className="py-2.5 pr-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      j.status === "complete" ? "bg-emerald-50 text-emerald-700"
                      : j.status === "failed" ? "bg-red-50 text-red-700"
                      : "bg-amber-50 text-amber-700"}`}>
                      {j.status}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 text-gray-600">{j.processed_vins}/{j.total_vins}</td>
                  <td className="py-2.5 pr-3 font-semibold" style={{ color: STATE_COLORS.recall_found }}>{j.recall_found_count}</td>
                  <td className="py-2.5 pr-3" style={{ color: STATE_COLORS.clear }}>{j.clear_count}</td>
                  <td className="py-2.5 pr-3" style={{ color: STATE_COLORS.pending }}>{j.pending_count}</td>
                  <td className="py-2.5 pr-3 text-gray-500">{fmt(j.created_at)}</td>
                  <td className="py-2.5 text-right">
                    {j.status === "complete" && (
                      <button
                        type="button" onClick={() => downloadReport(j.id)}
                        className="inline-flex items-center gap-1.5 text-[12px] text-blue-700 hover:underline"
                        data-testid={`recall-job-download-${j.id}`}
                      >
                        <Download size={13} /> Report
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Leads */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h3 className="text-[14px] font-semibold text-gray-900">Recall leads</h3>
        <p className="text-[12px] text-gray-500 mt-1 mb-3">
          Every customer who asked for a recall repair, and every open-recall vehicle found in a
          batch check.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="recall-leads-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
                <th className="py-2 pr-3">Customer</th>
                <th className="py-2 pr-3">Contact</th>
                <th className="py-2 pr-3">Vehicle</th>
                <th className="py-2 pr-3">VIN</th>
                <th className="py-2 pr-3">Campaigns</th>
                <th className="py-2 pr-3">From</th>
                <th className="py-2 pr-3">When</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-gray-400">No recall leads yet.</td></tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-gray-50" data-testid={`recall-lead-row-${l.id}`}>
                  <td className="py-2.5 pr-3 font-medium text-gray-900">{l.customer_name || "—"}</td>
                  <td className="py-2.5 pr-3 text-gray-600">{l.customer_phone || l.customer_email || "—"}</td>
                  <td className="py-2.5 pr-3 text-gray-600">{l.vehicle_desc || "—"}</td>
                  <td className="py-2.5 pr-3 font-mono text-[11.5px] text-gray-500">{l.vin || "—"}</td>
                  <td className="py-2.5 pr-3 text-gray-600 max-w-[220px]">
                    {(() => {
                      const list = (l.campaign_numbers || "").split(",").map((s) => s.trim()).filter(Boolean);
                      if (!list.length) return "—";
                      const head = list.slice(0, 3).join(", ");
                      return list.length > 3 ? `${head} +${list.length - 3} more` : head;
                    })()}
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className="inline-block rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-[11px] font-semibold">
                      {l.surface}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 text-gray-500">{fmt(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
